/**
 * Catalogue des offres — miroir d'affichage de payments_service/pricing.py
 * + formules Prémium affichées sur « Notre approche ».
 *
 * Les montants basique (persona / duo / pro) sont facturables via l’API paiement.
 * Les formules Prémium sont proposées au tunnel diagnostic ; le paiement réel
 * basique reste la source de vérité serveur pour LKM.
 */

export const ACCESS_INCOME_MIN_FCFA = 500000;
export const ACCESS_INCOME_MAX_FCFA = 749999;
export const ACCESS_PRICE_FCFA = 190000;
export const ACCESS_REVENU_TRANCHE = '500k_1m';

// En dessous de 500 000 FCFA/mois : aucune offre payante proposée. Le refus
// est doux (message d'accompagnement, pas un rejet brut) — voir
// isLowIncome()/offersForIncome() ci-dessous et DiagnosticOffers.vue.
export const LOW_INCOME_MAX_FCFA = 499999;
export const LOW_INCOME_REVENU_TRANCHE = '0_500k';

export const OFFERS = [
  {
    id: 'access',
    label: 'Persona Accès',
    audience: 'Revenu 500 000 – 749 999 FCFA',
    pitch: 'Un accompagnement d’entrée, en un seul règlement.',
    total: ACCESS_PRICE_FCFA,
    upfront: ACCESS_PRICE_FCFA,
    monthly: 0,
    features: [
      'Diagnostic patrimonial complet',
      'Plan d’action personnalisé',
      'Tableau de bord 7j/7',
      'Paiement unique — 190 000 FCFA',
    ],
    payable: true,
    uniquePrice: true,
  },
  {
    id: 'persona',
    label: 'Persona Basique',
    audience: 'Individuel',
    pitch: 'Votre patrimoine passé au crible, avec un plan d’action personnalisé.',
    total: 387200,
    totalnet: 414000,
    upfront: 190000,
    monthly: 24500,
    instalments: { monthly: 24500 },
    features: [
      'Diagnostic patrimonial complet',
      'Plan d’investissement personnalisé',
      '2 appels annuels avec votre conseiller',
      '2 projets accompagnés / an',
      'Tableau de bord 7j/7',
    ],
    payable: true,
  },
  {
    id: 'duo',
    label: 'Duo Basique',
    audience: 'Couple',
    pitch: 'Deux analyses complètes et une stratégie commune pour le foyer.',
    total: 619200,
    totalnet: 774000,
    upfront: 360000,
    monthly: 34500,
    instalments: { monthly: 34500 },
    features: [
      'Diagnostic patrimonial complet pour chacun',
      'Plan d’investissement personnalisé',
      '4 appels annuels avec votre conseiller',
      '2 projets accompagnés / an',
      'Tableau de bord 7j/7',
    ],
    payable: true,
  },
  {
    id: 'pro',
    label: 'Pro Basique',
    audience: 'Entrepreneurs et professions libérales',
    pitch: 'L’articulation entre votre activité et votre patrimoine personnel.',
    total: 666669,
    totalnet: 700000,
    upfront: 300000,
    monthly: 400000,
    features: [
      'Valorisation d’entreprise',
      'Conseils sur projet d\'entreprise',
      'Assistance personnalisée',
      '4 appels annuels avec votre conseiller',
      'Tableau de bord 7j/7',
    ],
    note: 'Un conseiller vous contacte après le règlement pour cadrer le périmètre de votre activité.',
    payable: true,
  },
  {
    id: 'persona_premium',
    label: 'Persona Prémium',
    audience: 'Individuel',
    pitch: 'L’offre Persona enrichie scoring, santé et financement de projets.',
    total: 425600,
    totalnet: 532000,
    upfront: 190000,
    monthly: 28500,
    instalments: { monthly: 28500 },
    features: [
      'Offre Persona basique +',
      'Credit Scoring',
      'Téléconsultation médicale',
      'Financement projet par levée de fonds',
      'Teaser projets',
      'Teaser emprunteur',
      'Accompagnement à l’assainissement des dettes',
    ],
    highlight: true,
    payable: false,
  },
  {
    id: 'duo_premium',
    label: 'Duo Prémium',
    audience: 'Couple',
    pitch: 'L’offre Duo enrichie scoring, santé et financement de projets.',
    total: 657600,
    totalnet: 822000,
    upfront: 360000,
    monthly: 38500,
    instalments: { monthly: 38500 },
    features: [
      'Offre Duo basique +',
      'Credit Scoring',
      'Téléconsultation médicale',
      'Financement projet par levée de fonds',
      'Teaser projets',
      'Teaser emprunteur',
      'Accompagnement à l’assainissement des dettes',
    ],
    payable: false,
  },
  {
    id: 'pro_premium',
    label: 'Pro Prémium',
    audience: 'Entrepreneurs et professions libérales',
    pitch: 'L’offre Pro enrichie valorisation et structuration financière.',
    total: 773334,
    totalnet: 800000,
    upfront: 300000,
    monthly: 500000,
    features: [
      'Offre Pro basique +',
      'Teaser de levée de fonds',
      'Structuration d’opération financière',
      '2 % de commission sur les levées de fonds (au lieu de 3 %)',
    ],
    note: 'Un conseiller vous contacte après le règlement pour cadrer le périmètre de votre activité.',
    payable: false,
  },
];

/** Offres acceptées par /api/payments/* (aligné payments_service/pricing.py). */
export const PAYABLE_OFFERS = OFFERS.filter((o) => o.payable !== false && ['persona', 'duo', 'pro'].includes(o.id));

const OFFER_BY_ID = OFFERS.reduce((acc, offer) => {
  acc[offer.id] = offer;
  return acc;
}, {});

export function normalizeOffer(offer) {
  const o = String(offer || 'persona').trim().toLowerCase();
  return o in OFFER_BY_ID ? o : 'persona';
}

/** Normalise vers une offre facturable API (les prémium retombent sur la basique du même segment). */
export function normalizePayableOffer(offer) {
  const o = normalizeOffer(offer);
  if (o === 'access' || o === 'persona_premium') return 'persona';
  if (o === 'duo_premium') return 'duo';
  if (o === 'pro_premium') return 'pro';
  if (['persona', 'duo', 'pro'].includes(o)) return o;
  return 'persona';
}

export function isPremiumOffer(offer) {
  return String(normalizeOffer(offer)).endsWith('_premium');
}

/** Famille d’offre (persona | duo | pro), indépendante de la formule. */
export function offerFamily(offer) {
  return normalizePayableOffer(offer);
}

/** Formule API : `premium` si le backend l’indique ou si l’id catalogue est *_premium. */
export function formuleFromApi({ type_offre, formule } = {}) {
  const f = String(formule || '').trim().toLowerCase();
  if (f === 'premium') return 'premium';
  if (isPremiumOffer(type_offre)) return 'premium';
  return 'basique';
}

/** Id catalogue pour les libellés (Persona Prémium, Duo Basique, …). */
export function catalogOfferId(family, formule) {
  const f = offerFamily(family);
  if (String(formule || '').toLowerCase() === 'premium') {
    if (f === 'duo') return 'duo_premium';
    if (f === 'pro') return 'pro_premium';
    return 'persona_premium';
  }
  return f;
}

export function isAccessIncome({ salaire, revenu } = {}) {
  const n = Number(salaire);
  if (Number.isFinite(n) && n > 0) {
    return n >= ACCESS_INCOME_MIN_FCFA && n <= ACCESS_INCOME_MAX_FCFA;
  }
  return String(revenu || '').trim() === ACCESS_REVENU_TRANCHE;
}

export function isLowIncome({ salaire, revenu } = {}) {
  const n = Number(salaire);
  if (Number.isFinite(n) && n > 0) {
    return n <= LOW_INCOME_MAX_FCFA;
  }
  return String(revenu || '').trim() === LOW_INCOME_REVENU_TRANCHE;
}

export function offersForIncome({ salaire, revenu } = {}) {
  if (isLowIncome({ salaire, revenu })) {
    return [];
  }
  if (isAccessIncome({ salaire, revenu })) {
    return OFFERS.filter((o) => o.id === 'access');
  }
  return OFFERS.filter((o) => o.id !== 'access');
}

export function getOffer(offer) {
  return OFFER_BY_ID[normalizeOffer(offer)];
}

export function offerLabel(offer) {
  return getOffer(offer).label;
}

/** Formules disponibles pour une offre : « total » plus les échéanciers proposés. */
export function allowedPlansForOffer(offer) {
  const instalments = getOffer(offer).instalments || {};
  return ['total', ...Object.keys(instalments).sort().reverse()];
}
